/**
 * @fileoverview ONE_SHOT plan helpers.
 * @summary Helper functions for ONE_SHOT plan expiration and validity checks.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/oneShotHelpers
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { getLocaleFromLanguage } from './i18n/index.js'
import { SubscriptionType } from '@karaonow/shared'

/**
 * Calculate the number of days remaining before a ONE_SHOT event expires.
 * @param expiresAt - The expiration date (string or Date)
 * @returns Number of days remaining (0 if expired or invalid date)
 */
export function getOneShotDaysRemaining(expiresAt?: string | Date): number {
  if (!expiresAt) return 0

  const expiration = new Date(expiresAt)
  const now = new Date()

  if (isNaN(expiration.getTime())) return 0 // Invalid date

  const diffMs = expiration.getTime() - now.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  return diffDays > 0 ? diffDays : 0
}

/**
 * Check if a ONE_SHOT event has expired.
 * @param expiresAt - The expiration date (string or Date)
 * @returns true if expired, false otherwise
 */
export function isOneShotExpired(expiresAt?: string | Date): boolean {
  if (!expiresAt) return false

  const expiration = new Date(expiresAt)
  const now = new Date()

  if (isNaN(expiration.getTime())) return false // Invalid date

  return now > expiration
}

/**
 * Format expiration date for display.
 * @param expiresAt - The expiration date (string or Date)
 * @param langCode - i18n language code (e.g., 'fr', 'en'). Defaults to 'fr'.
 * @returns Formatted date string (e.g., "15 janvier 2026")
 */
export function formatOneShotExpirationDate(expiresAt?: string | Date, langCode: string = 'fr'): string {
  if (!expiresAt) return ''

  const expiration = new Date(expiresAt)

  if (isNaN(expiration.getTime())) return '' // Invalid date

  const locale = getLocaleFromLanguage(langCode)
  return expiration.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Check if ONE_SHOT expiration should be displayed for an event.
 * @param event - The event object (must have planType and oneShotExpiresAt)
 * @returns true if expiration should be displayed, false otherwise
 */
export function shouldShowOneShotExpiration(event?: { planType?: string; oneShotExpiresAt?: string | Date }): boolean {
  return !!(event?.planType === SubscriptionType.ONE_SHOT && event?.oneShotExpiresAt)
}

